import React, { useState, useEffect, useRef } from 'react';
import './LeadForm.css';
import backgroundImage from '../images/Helper.png';
import logoImage from '../images/logo.svg';
import DatePickerInput from './form-elements/DatePickerInput';
import Select from './form-elements/Select';
import ScheduleInput from './form-elements/ScheduleInput';
import SalaryInput from './form-elements/SalaryInput';
import SuccessPage from './SuccessPage';
import { useNavigate } from 'react-router-dom';
import { useTelegramSDK } from '../utils/telegramSDK';
import {
  CITIZENSHIP_OPTIONS,
  EDUCATION_OPTIONS,
  LANGUAGE_OPTIONS,
  LANGUAGE_LEVELS,
  GENDER_OPTIONS,
  FORM_CATEGORIES
} from '../constants/formOptions';

// Импорт новых компонентов форм
import NannySearchForm from './forms/NannySearchForm';
import TeacherSearchForm from './forms/TeacherSearchForm';
import HousekeeperForm from './forms/HousekeeperForm';
import NannyApplicationForm from './forms/NannyApplicationForm';

// Маппинг полей между формами поиска
const SEARCH_FORM_CATEGORIES = ['nanny-search', 'teacher-search', 'housekeeper'];
const FIELD_MAP = {
  fullName: 'fullName',
  location: 'location',
  contacts: 'contacts',
  childrenInfo: 'childrenInfo',
  houseInfo: 'houseInfo',
  schedule: 'schedule',
  duties: 'duties',
  requirements: 'requirements',
  startDate: 'startDate',
  salary: 'salary',
};

function LeadForm() {
  const navigate = useNavigate();
  const { 
    isInitialized, 
    error, 
    user, 
    getUserFullName,
    showPopup,
    submitForm 
  } = useTelegramSDK();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    experience: '',
    skills: '',
    about: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const prevCategoryRef = useRef(null);

  // --- Добавляем refs для обязательных полей nanny-search ---
  const nannyFieldRefs = {
    location: useRef(null),
    childrenInfo: useRef(null),
    schedule: useRef(null),
    duties: useRef(null),
    fullName: useRef(null),
    contacts: useRef(null)
  };

  // --- Добавляем refs для обязательных полей teacher-search ---
  const teacherFieldRefs = {
    location: useRef(null),
    childrenInfo: useRef(null),
    schedule: useRef(null),
    duties: useRef(null),
    fullName: useRef(null),
    contacts: useRef(null)
  };

  // --- Добавляем refs для обязательных полей housekeeper ---
  const housekeeperFieldRefs = {
    location: useRef(null),
    houseInfo: useRef(null),
    schedule: useRef(null),
    duties: useRef(null),
    fullName: useRef(null),
    contacts: useRef(null)
  };

  // --- Добавляем refs для nanny-application ---
  const nannyApplicationFieldRefs = {
    name: useRef(null),
    birthDate: useRef(null),
    citizenship: useRef(null),
    education: useRef(null),
    location: useRef(null),
    languages: useRef(null),
    experience: useRef(null),
    skills: useRef(null),
    aboutMe: useRef(null),
    desiredSchedule: useRef(null),
    desiredSalary: useRef(null),
    contacts: useRef(null)
  };

  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        fullName: getUserFullName(),
        telegramId: user.id,
        username: user.username
      }));
    }
  }, [user]);

  useEffect(() => {
    if (selectedCategory) {
      // Проверяем высоту контента и контейнера
      const form = document.querySelector('.lead-form-with-header');
      const formContent = form?.scrollHeight;
      const viewportHeight = window.innerHeight;
      const body = document.body;
      const app = document.querySelector('.App');
      
      console.log({
        'Категория': selectedCategory,
        'Высота контента формы': formContent,
        'Высота viewport': viewportHeight,
        'Разница': formContent - viewportHeight,
        'Стили overflow body': window.getComputedStyle(body).overflow,
        'Стили overflow App': window.getComputedStyle(app).overflow,
        'Стили position форма': window.getComputedStyle(form).position,
        'CSS переменные темы': {
          'button-color': getComputedStyle(document.documentElement).getPropertyValue('--tg-theme-button-color'),
          'bg-color': getComputedStyle(document.documentElement).getPropertyValue('--tg-theme-bg-color'),
          'text-color': getComputedStyle(document.documentElement).getPropertyValue('--tg-theme-text-color')
        },
        'Размеры формы': {
          width: form?.offsetWidth,
          height: form?.offsetHeight,
          clientWidth: form?.clientWidth,
          clientHeight: form?.clientHeight,
          scrollWidth: form?.scrollWidth,
          scrollHeight: form?.scrollHeight
        },
        'Видимость формы': {
          display: window.getComputedStyle(form).display,
          visibility: window.getComputedStyle(form).visibility,
          opacity: window.getComputedStyle(form).opacity
        }
      });
    }
  }, [selectedCategory]);

  useEffect(() => {
    console.log('showSuccess:', showSuccess);
    // alert('Успешно отправлено!'); // временно для отладки
  }, [showSuccess]);

  // Кэширование формы поиска (и теперь также nanny)
  useEffect(() => {
    // Список пользовательских полей для проверки (можно расширить при необходимости)
    const userFields = [
      'name', 'fullName', 'birthDate', 'citizenship', 'education', 'location', 'languages', 'experience', 'skills', 'aboutMe', 'desiredSchedule', 'desiredSalary', 'contacts',
      'childrenInfo', 'houseInfo', 'schedule', 'duties', 'requirements', 'startDate', 'salary', 'surveyAnswers', 'about', 'phone', 'email'
    ];
    const hasUserData = userFields.some(field => {
      const value = formData[field];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
      return value !== undefined && value !== null && value !== '';
    });
    if ((SEARCH_FORM_CATEGORIES.includes(selectedCategory) || selectedCategory === 'nanny') && hasUserData) {
      localStorage.setItem(`leadForm-${selectedCategory}`, JSON.stringify(formData));
    }
  }, [formData, selectedCategory]);

  // Загрузка кэша и перенос данных между формами поиска
  useEffect(() => {
    if (SEARCH_FORM_CATEGORIES.includes(selectedCategory)) {
      // Загружаем кэш для выбранной формы
      const cached = localStorage.getItem(`leadForm-${selectedCategory}`);
      console.log('Загрузка кэша для поисковой формы', selectedCategory, cached);
      if (cached) {
        setFormData(JSON.parse(cached));
      } else {
        // Если кэша нет, ищем кэш в других поисковых формах
        let found = false;
        for (const cat of SEARCH_FORM_CATEGORIES) {
          if (cat !== selectedCategory) {
            const otherCache = localStorage.getItem(`leadForm-${cat}`);
            if (otherCache) {
              const otherData = JSON.parse(otherCache);
              // Переносим совпадающие поля
              const newData = {};
              for (const [to, from] of Object.entries(FIELD_MAP)) {
                let value = otherData[from];
                // Исправление: location должен быть объектом { address: ... }
                if (to === 'location') {
                  if (typeof value === 'string') {
                    value = { address: value };
                  }
                  // Если value уже объект, оставляем как есть
                }
                if (value !== undefined) {
                  newData[to] = value;
                }
              }
              setFormData(newData);
              found = true;
              break;
            }
          }
        }
        if (!found) {
          setFormData({});
        }
      }
    }
    // Для формы кандидата — только кэш, без переноса, location всегда объект
    if (selectedCategory === 'nanny') {
      const cached = localStorage.getItem('leadForm-nanny');
      console.log('Загрузка кэша для nanny', cached);
      if (cached) {
        setFormData(prev => {
          const parsed = JSON.parse(cached);
          // location всегда объект
          if (typeof parsed.location === 'string') {
            parsed.location = { address: parsed.location };
          }
          if (!parsed.location || typeof parsed.location !== 'object') {
            parsed.location = { address: '' };
          }
          // education всегда строка
          if (typeof parsed.education === 'object' && parsed.education !== null) {
            parsed.education = parsed.education.level || '';
          }
          if (JSON.stringify(prev) === JSON.stringify(parsed)) {
            return prev;
          }
          return parsed;
        });
      } else {
        setFormData(prev => {
          if (Object.keys(prev).length === 0) return prev;
          return {};
        });
      }
    }
    prevCategoryRef.current = selectedCategory;
  }, [selectedCategory]);

  // При смене категории сбрасываем isSubmitted
  useEffect(() => {
    setIsSubmitted(false);
  }, [selectedCategory]);

  // Если приложение не инициализировано, показываем сообщение об ошибке
  if (!isInitialized) {
    return (
      <div className="lead-form-main">
        <div className="error-message">
          {error || 'Ошибка инициализации приложения'}
        </div>
      </div>
    );
  }

  // Функция валидации поля
  const validateField = (name, value, category) => {
    switch (name) {
      // Общие поля для всех форм
      case 'location':
        return !value?.address ? 'Укажите локацию' : '';
      case 'duties':
        // Для всех категорий теперь минимум 5 символов
        return !value || value.length < 5 ? 'Минимум 5 символов' : '';
      case 'schedule':
        if (!value) return 'Выберите график работы';
        
        // Проверяем тип графика
        switch (value.scheduleType) {
          case 'custom':
            // Для произвольного графика нужны и дни, и часы
            if (!value.days?.length) return 'Выберите дни недели';
            if (!value.hours) return 'Укажите количество часов';
            return '';
          
          case '5_2':
          case '6_1':
            // Для фиксированных графиков нужны только часы
            if (!value.hours) return 'Укажите количество часов';
            return '';
          
          case 'shift':
            // Для вахты нужны детали смены
            if (!value.shiftDetails) return 'Укажите детали вахты';
            return '';
          
          default:
            return 'Выберите график работы';
        }
      case 'fullName':
      case 'name':
        return !value || value.length < 2 ? 'Укажите имя и фамилию' : '';
      case 'contacts':
        return !value ? 'Укажите контактные данные' : '';
      
      // Специфичные поля для разных форм
      case 'childrenInfo':
        if (category === 'nanny-search' || category === 'teacher-search') {
          if (!value || value.length === 0) return 'Добавьте информацию о детях';
          return value.some(child => child.age === undefined || child.age === null || child.age === '') ? 'Укажите возраст ребенка' : '';
        }
        return '';
      
      case 'houseInfo':
        if (category === 'housekeeper') {
          if (!value) return 'Заполните информацию о помещении';
          if (!value.rooms) return 'Укажите количество комнат';
          if (!value.area) return 'Укажите площадь';
          if (!value.residents) return 'Укажите количество проживающих';
        }
        return '';
      
      // Поля для анкеты кандидата
      case 'birthDate':
        return !value ? 'Укажите дату рождения' : '';
      case 'citizenship':
        return !value ? 'Укажите гражданство' : '';
      case 'education':
        return !value ? 'Укажите уровень образования' : '';
      case 'languages':
        return !value ? 'Укажите языки и уровень владения' : '';
      case 'experience':
        return !value ? 'Опишите ваш опыт работы' : '';
      case 'skills':
        return !value ? 'Укажите ваши навыки' : '';
      case 'aboutMe':
        return !value ? 'Расскажите о себе' : '';
      case 'desiredSchedule':
        return !value ? 'Укажите желаемый график работы' : '';
      case 'desiredSalary':
        return !value ? 'Укажите желаемую оплату' : '';
      case 'startDate':
        // Не обязательное поле
        return '';
      
      default:
        return '';
    }
  };

  // Обновляем handleInputChange
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Изменение поля ${name}: ${value}`);
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Валидируем поле при изменении, но ошибки показываем только если isSubmitted
    if (isSubmitted) {
      const error = validateField(name, value, selectedCategory);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Валидация всей формы перед отправкой
  const validateForm = () => {
    const newErrors = {};
    let requiredFields;

    switch (selectedCategory) {
      case 'nanny-search':
      case 'teacher-search':
        requiredFields = ['location', 'childrenInfo', 'duties', 'schedule', 'fullName', 'contacts'];
        break;
      case 'housekeeper':
        requiredFields = ['location', 'houseInfo', 'duties', 'schedule', 'fullName', 'contacts'];
        break;
      case 'nanny':
        requiredFields = [
          'name', 'birthDate', 'citizenship', 'education', 'location',
          'languages', 'experience', 'skills', 'aboutMe', 'desiredSchedule',
          'desiredSalary', 'startDate', 'contacts'
        ];
        break;
      default:
        requiredFields = [];
    }
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field], selectedCategory);
      if (error) {
        newErrors[field] = error;
      }
    });

    // === Валидация опросника для nanny ===
    if (selectedCategory === 'nanny') {
      const allSurveyKeys = [
        'q1_1','q1_2','q1_3','q2_1','q2_2','q2_3','q3_1','q3_2','q3_3',
        'q4_1','q4_2','q4_3','q5_1','q5_2','q5_3','q6_1','q6_2','q6_3'
      ];
      const answers = formData.surveyAnswers || {};
      const missing = allSurveyKeys.filter(key => !answers[key]);
      if (missing.length > 0) {
        newErrors.surveyAnswers = 'Пожалуйста, ответьте на все вопросы опросника';
      }
    }

    return newErrors;
  };

  const handleChildInfoChange = (index, field, value) => {
    const newChildrenInfo = [...(formData.childrenInfo || [])];
    if (!newChildrenInfo[index]) {
      newChildrenInfo[index] = {};
    }
    newChildrenInfo[index][field] = value;
    setFormData(prevState => ({
      ...prevState,
      childrenInfo: newChildrenInfo
    }));

    // Валидация childrenInfo при изменении, если форма уже отправлялась
    if (isSubmitted && (selectedCategory === 'nanny-search' || selectedCategory === 'teacher-search')) {
      const error = validateField('childrenInfo', newChildrenInfo, selectedCategory);
      setErrors(prev => ({
        ...prev,
        childrenInfo: error
      }));
    }
  };

  const addChild = () => {
    console.log('Добавлен новый ребенок');
    setFormData(prevState => ({
      ...prevState,
      childrenInfo: [...(prevState.childrenInfo || []), {}]
    }));
  };

  const removeChild = (index) => {
    console.log(`Удален ребенок с индексом ${index}`);
    setFormData(prevState => ({
      ...prevState,
      childrenInfo: prevState.childrenInfo.filter((_, i) => i !== index)
    }));
  };

  // Добавляем функцию для проверки размера данных
  const checkDataSize = (data) => {
    const size = new Blob([JSON.stringify(data)]).size;
    const maxSize = 1024 * 1024; // 1 MB
    return size <= maxSize;
  };

  // Добавляем функцию для проверки обязательных полей
  const checkRequiredFields = (data, category) => {
    const commonFields = ['contacts'];
    const categoryFields = {
      'nanny-search': ['location', 'childrenInfo', 'duties', 'schedule', 'fullName'],
      'teacher-search': ['location', 'childrenInfo', 'duties', 'schedule', 'fullName'],
      'housekeeper': ['location', 'houseInfo', 'duties', 'schedule', 'fullName'],
      'nanny': ['name', 'birthDate', 'citizenship', 'education', 'location', 'languages', 'experience', 'skills', 'aboutMe', 'desiredSchedule', 'desiredSalary']
    };

    const requiredFields = [...commonFields, ...(categoryFields[category] || [])];
    const missingFields = requiredFields.filter(field => {
      const value = data[field];
      return !value || (Array.isArray(value) && value.length === 0) || (typeof value === 'object' && Object.keys(value).length === 0);
    });

    return missingFields;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    const newErrors = validateForm();
    setErrors(newErrors);

    // Универсальный скролл к ошибке для всех форм
    if (Object.keys(newErrors).length > 0) {
      let order = [];
      let refs = null;
      switch (selectedCategory) {
        case 'nanny-search':
          order = ['location', 'childrenInfo', 'schedule', 'duties', 'fullName', 'contacts'];
          refs = nannyFieldRefs;
          break;
        case 'teacher-search':
          order = ['location', 'childrenInfo', 'schedule', 'duties', 'fullName', 'contacts'];
          refs = teacherFieldRefs;
          break;
        case 'housekeeper':
          order = ['location', 'houseInfo', 'schedule', 'duties', 'fullName', 'contacts'];
          refs = housekeeperFieldRefs;
          break;
        case 'nanny':
          order = ['name', 'birthDate', 'citizenship', 'education', 'location', 'languages', 'experience', 'skills', 'aboutMe', 'desiredSchedule', 'desiredSalary', 'contacts'];
          refs = nannyApplicationFieldRefs;
          break;
        default:
          break;
      }
      if (refs) {
        for (const field of order) {
          if (newErrors[field] && refs[field]?.current) {
            refs[field].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            break;
          }
        }
      }
      return;
    }

    try {
      // Проверяем, что все обязательные поля заполнены
      const requiredFieldsValid = checkRequiredFields(formData, selectedCategory);
      if (!requiredFieldsValid) {
        showPopup('Пожалуйста, заполните все обязательные поля');
        return;
      }

      // Проверяем размер данных
      if (!checkDataSize(formData)) {
        showPopup('Превышен допустимый размер данных');
        return;
      }

      setIsSubmitting(true);

      // --- Исправление: salary и startDate ---
      let submitData = { ...formData };
      // salary: всегда отправлять currency и period, amount — если есть
      if (submitData.salary) {
        submitData.salary = {
          currency: submitData.salary.currency || 'AED',
          period: submitData.salary.period || 'day',
        };
        if (submitData.salary.amount) {
          submitData.salary.amount = Number(formData.salary.amount);
        }
      } else {
        submitData.salary = {
          currency: 'AED',
          period: 'day'
        };
      }
      // startDate: если это объект Date, преобразовать к строке; если пусто — не отправлять
      if (submitData.startDate instanceof Date) {
        submitData.startDate = submitData.startDate.toISOString().split('T')[0];
      }
      if (!submitData.startDate) {
        delete submitData.startDate;
      }
      // --- Исправление: для nanny-application education должен быть объектом ---
      if (selectedCategory === 'nanny' && typeof submitData.education === 'string') {
        submitData.education = { level: submitData.education };
      }

      const success = await submitForm(submitData, selectedCategory);
      if (success) {
        setIsSubmitting(false);
        setShowSuccess(true);
        setIsSubmitted(false); // Сбросить после успешной отправки
        return;
      }
    } catch (error) {
      console.error('Ошибка при отправке формы:', error);
      setIsSubmitting(false);
    }
  };

  // Добавляем обработчик для кнопки "Назад"
  const handleBack = () => {
    console.log('Нажата кнопка "Назад"');
    setSelectedCategory(null);
    setFormData({});
  };

  // Функция для инициализации formData по категории
  const getInitialFormData = (category) => {
    switch (category) {
      case 'nanny-search':
        return {
          location: { address: '' },
          childrenInfo: [{}],
          schedule: {},
          duties: '',
          requirements: '',
          startDate: '',
          salary: {},
          fullName: '',
          contacts: ''
        };
      case 'teacher-search':
        return {
          location: { address: '' },
          childrenInfo: [{}],
          schedule: {},
          duties: '',
          requirements: '',
          startDate: '',
          salary: {},
          fullName: '',
          contacts: ''
        };
      case 'housekeeper':
        return {
          location: { address: '' },
          houseInfo: {},
          schedule: {},
          duties: '',
          requirements: '',
          startDate: '',
          salary: {},
          fullName: '',
          contacts: ''
        };
      case 'nanny':
        return {
          name: '',
          birthDate: '',
          citizenship: '',
          education: { level: '' },
          location: { address: '' },
          languages: '',
          experience: '',
          skills: '',
          aboutMe: '',
          desiredSchedule: '',
          desiredSalary: '',
          startDate: '',
          contacts: ''
        };
      default:
        return {};
    }
  };

  const renderCategorySelection = () => (
    <>
      <div className="main-logo">
        <img src={logoImage} alt="Logo" />
      </div>
      <div className="category-buttons">
        <button 
          type="button" 
          onClick={() => {
            console.log('Выбрана категория: Нужна няня');
            setFormData(getInitialFormData('nanny-search'));
            setSelectedCategory('nanny-search');
          }}
          className="category-button"
        >
          Нужна няня
        </button>
        <button 
          type="button" 
          onClick={() => {
            console.log('Выбрана категория: Нужен учитель или репетитор');
            setFormData(getInitialFormData('teacher-search'));
            setSelectedCategory('teacher-search');
          }}
          className="category-button"
        >
          Нужен учитель или репетитор
        </button>
        <button 
          type="button" 
          onClick={() => {
            console.log('Выбрана категория: Нужна домработница');
            setFormData(getInitialFormData('housekeeper'));
            setSelectedCategory('housekeeper');
          }}
          className="category-button"
        >
          Нужна домработница
        </button>
        <button 
          type="button" 
          onClick={() => {
            console.log('Выбрана категория: Я ищу работу');
            setFormData(getInitialFormData('nanny'));
            setSelectedCategory('nanny');
          }}
          className="category-button"
        >
          Я ищу работу
        </button>
      </div>
      <div className="help-section help-section-main">
        <a 
          href="https://t.me/Svetik_Skywalker" 
          className="help-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Нужна помощь?
        </a>
      </div>
    </>
  );

  const renderForm = () => {
    const commonProps = {
      formData,
      errors,
      isSubmitting,
      handleInputChange,
      handleChildInfoChange,
      addChild,
      removeChild,
      handleSubmit,
      isSubmitted
    };

    try {
      if (!selectedCategory) {
        return (
          <div className="lead-form-main">
            {renderCategorySelection()}
          </div>
        );
      }

      let FormComponent, fieldRefs;
      switch (selectedCategory) {
        case 'nanny-search':
          FormComponent = NannySearchForm;
          fieldRefs = nannyFieldRefs;
          break;
        case 'teacher-search':
          FormComponent = TeacherSearchForm;
          fieldRefs = teacherFieldRefs;
          break;
        case 'housekeeper':
          FormComponent = HousekeeperForm;
          fieldRefs = housekeeperFieldRefs;
          break;
        case 'nanny':
          FormComponent = NannyApplicationForm;
          fieldRefs = nannyApplicationFieldRefs;
          break;
        default:
          return (
            <div className="lead-form-main">
              {renderCategorySelection()}
            </div>
          );
      }

      return (
        <div className="lead-form-with-header">
          <button onClick={handleBack} className="back-button">
            Назад
          </button>
          <FormComponent {...commonProps} fieldRefs={fieldRefs} />
        </div>
      );
    } catch (error) {
      console.error('Ошибка при рендеринге формы:', error);
      return (
        <div className="lead-form-main">
          <div className="error-message">
            Произошла ошибка при загрузке формы. Пожалуйста, попробуйте позже.
          </div>
          <button onClick={handleBack} className="back-button">
            Назад
          </button>
        </div>
      );
    }
  };

  if (showSuccess) {
    return <SuccessPage />;
  }

  return (
    <div className="lead-form">
      {renderForm()}
    </div>
  );
}

export default LeadForm;